import dayjs from 'dayjs';

import { isBoulderRate, BOULDER_RATE_SUMMARY } from '@m/constants/CustomRates';
import { SiteWithHoursAndRate } from '@m/types/api';
import { HoursOfOperation } from '@m/types/api/Site';
import { convertNumberToDollarFormat } from '@m/utils/convertNumberToDollarFormat';
import { clampReservationWindowToFuture, parseDeepLinkDateTime } from '@m/utils/dateTimeUtils';
import getCoordinateDistance from '@m/utils/getCoordinateDistance';
import { isDurationRate, isFlatRate, isTabularRate } from '@m/utils/guards/rateTypes';
import { parseDurationRate, parseFlatRate, parseTabularRate } from '@m/utils/rate/parseRate';
import { is24HourOperation, parseOperationTime } from 'apps/customer/utils/parseOperationTime';

import type {
  ReservationFilterOption,
  ReservationFiltersValue,
} from '@m/components-ui/reservation/reservation-filters-sheet';

/**
 * Builds venue-event badge copy from a numeric starting rate.
 * Returns undefined when the rate is missing so callers omit the badge
 * (same contract as marketing `formatEventParkingLabel`).
 */
export function formatParkingStartingAtLabel(rateStartsAt?: number): string | undefined {
  if (rateStartsAt == null || Number.isNaN(rateStartsAt)) return undefined;
  const formatted = convertNumberToDollarFormat({ price: rateStartsAt, includeZeros: false });
  return formatted ? `Parking starting at ${formatted}` : undefined;
}
export const DEFAULT_FOCUS_ZOOM = 14;
/** Radius used when committing a search / near-me / URL deep-link (miles). */
export const INITIAL_RADIUS_MILES = 25;
/** Floor for pan/zoom discover radius so the API never gets a near-zero circle. */
export const MIN_VIEWPORT_RADIUS_MILES = 0.5;

/** NE/SW rectangle of the interactive map, used to clip list + markers. */
export type VisibleMapBounds = {
  east: number;
  north: number;
  south: number;
  west: number;
};

/**
 * Map `visibleCoords` are `[NE, SW]` as `[lng, lat]` pairs (see `@m/components-ui/map/map`).
 */
export function visibleBoundsFromCoords(
  visibleCoords: [[number, number], [number, number]],
): VisibleMapBounds {
  const [ne, sw] = visibleCoords;
  return {
    north: ne[1],
    east: ne[0],
    south: sw[1],
    west: sw[0],
  };
}

export function approxRadiusMilesFromVisibleCoords(
  visibleCoords: [[number, number], [number, number]],
): number {
  const diagonalMiles = getCoordinateDistance(visibleCoords[0], visibleCoords[1]);
  return Math.max(MIN_VIEWPORT_RADIUS_MILES, Math.ceil(diagonalMiles / 2));
}

/**
 * True when every corner of the visible map rectangle already lies inside the
 * circle we last fetched from the API. Zoom-in / pan-within can then update the
 * list via client-side clipping only — no discover refetch.
 */
export function isViewportCoveredByFetchedCircle(
  bounds: VisibleMapBounds,
  fetched: { latitude: number; longitude: number; maxDistanceMiles: number },
): boolean {
  const corners: { lat: number; lng: number }[] = [
    { lat: bounds.north, lng: bounds.east },
    { lat: bounds.north, lng: bounds.west },
    { lat: bounds.south, lng: bounds.east },
    { lat: bounds.south, lng: bounds.west },
  ];
  const origin: [number, number] = [fetched.longitude, fetched.latitude];
  return corners.every(
    (corner) => getCoordinateDistance(origin, [corner.lng, corner.lat]) <= fetched.maxDistanceMiles,
  );
}

export function siteInVisibleBounds(
  site: { lat: number; lng: number },
  bounds: VisibleMapBounds,
): boolean {
  return (
    site.lat <= bounds.north &&
    site.lat >= bounds.south &&
    site.lng <= bounds.east &&
    site.lng >= bounds.west
  );
}

/** When `bounds` is null (no map event yet), return sites unchanged. */
export function filterSitesInVisibleBounds<T extends { lat: number; lng: number }>(
  sites: T[],
  bounds: VisibleMapBounds | null,
): T[] {
  if (!bounds) return sites;
  return sites.filter((site) => siteInVisibleBounds(site, bounds));
}

/** Search-bar label when browser geolocation is available and in use. */
export const PARKING_NEAR_ME = 'Parking near me';
/** Search-bar label when browsing the map without geolocation. */
export const PARKING_NEAR_MAP_AREA = 'Parking near map area';
/** Monthly search prompt, shown only before a destination or near-me search. */
export const MONTHLY_SEARCH_PLACEHOLDER = 'Enter your destination';

const SYNTHETIC_LOCATION_LABELS = new Set([
  PARKING_NEAR_ME,
  PARKING_NEAR_MAP_AREA,
  'Current location',
]);

/** Home "See parking near me" used to seed `lat.toFixed(5), lng.toFixed(5)` when no label was passed. */
const COORDINATE_LABEL_PATTERN = /^-?\d+\.\d{5}, -?\d+\.\d{5}$/;

/** True when the label is a geo/map browsing placeholder, not a typed address. */
export function isSyntheticLocationLabel(label: string | null | undefined): boolean {
  return !!label && (SYNTHETIC_LOCATION_LABELS.has(label) || COORDINATE_LABEL_PATTERN.test(label));
}

/**
 * The map-area label is filled in before the user searches. Hide that so the
 * monthly placeholder can show. A near-me search and typed addresses stay.
 */
export function searchQueryForParkingMode(query: string, monthly: boolean): string {
  if (!monthly || !query) return query;
  if (query === PARKING_NEAR_MAP_AREA || COORDINATE_LABEL_PATTERN.test(query)) return '';
  return query;
}

/**
 * Committed location label for discovery chrome (heading / summary).
 * Uses only `selectedSearchLoc` + geolocation — never draft typing in `searchQuery`.
 */
export function getDiscoverySearchBarLabel(options: {
  searchQuery: string;
  selectedSearchLoc: { address: string } | null;
  userLocation: { latitude: number; longitude: number } | null;
}): string {
  const { selectedSearchLoc, userLocation } = options;
  if (selectedSearchLoc && !isSyntheticLocationLabel(selectedSearchLoc.address)) {
    return selectedSearchLoc.address;
  }
  if (selectedSearchLoc?.address === PARKING_NEAR_ME) {
    return PARKING_NEAR_ME;
  }
  return userLocation ? PARKING_NEAR_ME : PARKING_NEAR_MAP_AREA;
}

/** Short committed place label (drops trailing address after the first comma). */
export function getShortLocationLabel(label: string): string {
  return label.includes(',') ? label.slice(0, label.indexOf(',')).trim() : label;
}

/** Sidebar / results heading, e.g. "Parking near 128 N 8th St" or "Parking near me". */
export function getParkingNearHeading(options: {
  searchQuery: string;
  selectedSearchLoc: { address: string } | null;
  userLocation: { latitude: number; longitude: number } | null;
}): string {
  const label = getDiscoverySearchBarLabel(options);
  if (label === PARKING_NEAR_ME || label === PARKING_NEAR_MAP_AREA) return label;
  return `Parking near ${getShortLocationLabel(label)}`;
}

/** True when the user has committed a real address (not near-me / map-area). */
export function hasEnteredSearchAddress(selectedSearchLoc: { address: string } | null): boolean {
  return !!selectedSearchLoc && !isSyntheticLocationLabel(selectedSearchLoc.address);
}

/** Mobile reservation flow: browse the map → pick a location → fill the form → view results. */
export type DiscoveryReservationPhase = 'browsing' | 'form' | 'results';

/**
 * Initial map chrome. Near-me / unlabeled lat-lng pins stay in browsing so
 * results chrome does not show a coordinate string and authenticated users
 * keep the landing-page back control. Real address searches land on results.
 */
export function getInitialReservationPhase(options: {
  hasDateWindow: boolean;
  reservationsEnabled: boolean;
  selectedSearchLoc: { address: string } | null;
  startInReservationForm: boolean;
  startInResults: boolean;
}): DiscoveryReservationPhase {
  if (options.reservationsEnabled && options.startInReservationForm) return 'form';
  if (options.hasDateWindow) return 'results';
  if (options.startInResults) return 'results';
  if (hasEnteredSearchAddress(options.selectedSearchLoc)) return 'results';
  return 'browsing';
}

/** Filters supported by the metadata returned from `/zones/discover`. */
export const DISCOVER_FACILITY_FILTER_OPTIONS: ReservationFilterOption[] = [
  { label: 'EV Charger', value: 'ev-charger' },
  { label: 'In and Out Access', value: 'in-and-out-access' },
];

const DISCOVER_FACILITY_FILTER_VALUES = new Set(
  DISCOVER_FACILITY_FILTER_OPTIONS.map(({ value }) => value),
);

export function getDiscoverCompatibleFilters(
  filters: ReservationFiltersValue,
): ReservationFiltersValue {
  return {
    lotFacilities: filters.lotFacilities.filter((value) =>
      DISCOVER_FACILITY_FILTER_VALUES.has(value),
    ),
    parkingTypes: [],
  };
}

export type SiteWithWalk = {
  distanceMiles: number | null;
  site: SiteWithHoursAndRate;
  walkDistanceFormatted: string | null;
  walkTimeMinutes: number | null;
};

export function filterSitesWithWalkInVisibleBounds(
  entries: SiteWithWalk[],
  bounds: VisibleMapBounds | null,
): SiteWithWalk[] {
  if (!bounds) return entries;
  return entries.filter((entry) => siteInVisibleBounds(entry.site, bounds));
}

/**
 * Reservation window + filter controls shared by the desktop and mobile discovery
 * views. The committed window state and filters live in `DiscoveryFullscreenView`
 * (the single owner that feeds `useDiscoveryData`); both views receive these to
 * render their pickers, the search summary, and the filters sheet.
 */
export interface ReservationControlsProps {
  endDateTime?: Date;
  filterCount: number;
  filters: ReservationFiltersValue;
  onApplyFilters: (filters: ReservationFiltersValue) => void;
  onClearDateRange?: () => void;
  /** Set both ends of the window without start→end +2h side effects (details card sync). */
  onCommitDateRange?: (start: Date, end: Date) => void;
  onCommitEnd: (date: Date) => void;
  onCommitStart: (date: Date) => void;
  startDateTime?: Date;
}

export function getSiteRateValue(site: SiteWithHoursAndRate): number {
  const { rate } = site;
  if (isFlatRate(rate)) {
    return rate.priceWithFee ?? rate.price;
  } else if (isDurationRate(rate)) {
    return rate.pricePerPeriodWithFee ?? rate.pricePerPeriod;
  } else if (isTabularRate(rate)) {
    const firstStep = rate.tabularSteps?.[0];
    if (
      firstStep &&
      (firstStep.amountWithFee ?? firstStep.amount) > 0 &&
      firstStep.cumulativePeriod > 0
    ) {
      return (firstStep.amountWithFee ?? firstStep.amount) / firstStep.cumulativePeriod;
    }
    return rate.pricePerPeriod;
  }
  return 0;
}

function readMonthlyPrice(site: SiteWithHoursAndRate): {
  priceFormatted: string;
  rateLabel: string;
} | null {
  if (!('monthlyPriceFormatted' in site)) return null;
  const price = (site as { monthlyPriceFormatted?: string }).monthlyPriceFormatted;
  if (!price) return null;
  const rateLabel = (site as { monthlyRateLabel?: string }).monthlyRateLabel || 'per month';
  return { priceFormatted: price, rateLabel };
}

export function formatSiteRateAndLabel(
  site: SiteWithHoursAndRate,
  options?: { asTotal?: boolean },
): {
  priceFormatted: string;
  rateLabel: string;
} {
  const monthlyPrice = readMonthlyPrice(site);
  if (monthlyPrice) return monthlyPrice;

  let result: { priceFormatted: string; rateLabel: string };

  if (isBoulderRate(site.id)) {
    result = { ...BOULDER_RATE_SUMMARY };
  } else {
    const { rate } = site;
    if (isTabularRate(rate)) {
      const { tabularSteps } = parseTabularRate(rate, { priceIncludeZeros: true });
      const firstStep = tabularSteps[0];
      if (!firstStep) {
        result = { priceFormatted: '', rateLabel: '' };
      } else {
        result = {
          priceFormatted:
            firstStep.dynamicPriceText ||
            (firstStep.priceWithFee > 0 ? firstStep.priceWithFeeText : firstStep.priceText) ||
            '',
          rateLabel: `up to ${firstStep.cumulativePeriodInterval} ${firstStep.cumulativePeriodUnitText}`,
        };
      }
    } else if (isFlatRate(rate)) {
      const { maxPeriod } = parseFlatRate(rate, { priceIncludeZeros: true });
      result = {
        priceFormatted:
          maxPeriod.dynamicPriceText ||
          (maxPeriod.priceWithFee > 0 ? maxPeriod.priceWithFeeText : maxPeriod.priceText) ||
          '',
        rateLabel: `up to ${maxPeriod.interval} ${maxPeriod.unitText}`,
      };
    } else if (isDurationRate(rate)) {
      const { period } = parseDurationRate(rate, { priceIncludeZeros: true });
      result = {
        priceFormatted:
          period.dynamicPriceText ||
          (period.priceWithFee > 0 ? period.priceWithFeeText : period.priceText) ||
          '',
        rateLabel:
          period.interval === 1
            ? `per ${period.unitText}`
            : `per ${period.interval} ${period.unitText}`,
      };
    } else {
      result = { priceFormatted: '', rateLabel: '' };
    }
  }

  // Reservation date window: the quoted price is for the selected stay — label as Total.
  if (options?.asTotal && result.priceFormatted) {
    return { priceFormatted: result.priceFormatted, rateLabel: 'Total' };
  }
  return result;
}

export function formatSiteRate(site: SiteWithHoursAndRate): string {
  return formatSiteRateAndLabel(site).priceFormatted;
}

/** Pin label: monthly price when the site came from subscription search, otherwise the stay quote. */
export function formatMarkerPrice(
  site: SiteWithHoursAndRate,
  showStayPrice: boolean,
): string | undefined {
  const monthly = readMonthlyPrice(site);
  if (monthly) return monthly.priceFormatted;
  if (!showStayPrice) return undefined;
  return formatSiteRate(site) || undefined;
}

/**
 * Whether a reservation can be booked for a site: the global `WebFirstPartyReservations`
 * Statsig gate must be on for the user, AND the zone itself must accept first-party
 * reservations. `firstPartyReservationsEnabled` comes from zones/discover or the
 * availability API — when absent (unknown), treat as enabled rather than disabled.
 */
export function siteReservationsAvailable(
  site: SiteWithHoursAndRate,
  reservationsEnabled: boolean,
): boolean {
  return reservationsEnabled && site.firstPartyReservationsEnabled !== false;
}

// TODO: Uses browser local time — may be inaccurate if viewer is in a different timezone than the lot
export function getSiteIsOpen(hoursOfOperation?: HoursOfOperation | null): boolean | undefined {
  if (!hoursOfOperation?.today) return undefined;
  const now = new Date();
  const [openH, openM] = hoursOfOperation.today.open.split(':').map(Number);
  const [closeH, closeM] = hoursOfOperation.today.close.split(':').map(Number);

  if (Number.isNaN(openH) || Number.isNaN(openM) || Number.isNaN(closeH) || Number.isNaN(closeM)) {
    return undefined;
  }

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = openH * 60 + openM;
  const closeMinutes = closeH * 60 + closeM;

  // Same-day window: close is after open (e.g., 08:00–18:00)
  if (closeMinutes > openMinutes) {
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  }

  // Overnight/24h window: close is at/before open (e.g., 19:00–06:00, or 07:00–06:59)
  return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
}

export function formatClosingTime(time?: string): string | undefined {
  if (!time) return undefined;
  const parsed = parseOperationTime(time);
  if (!parsed) return undefined;
  return parsed.toFormat('h:mma').toLowerCase();
}

export { is24HourOperation };

// Builds the navigation query for a site. Prefers the full street address,
// falling back to "lat,lng" when address fields are absent (e.g. sites sourced
// from the parking-availability API, which returns coordinates but no address).
export function buildDirectionsQuery(site: SiteWithHoursAndRate): string {
  // Only use the address form when every part is present — a partial address
  // (e.g. availability-API sites before rates enrichment) would otherwise emit
  // "undefined" segments. Fall back to coordinates in that case.
  if (site.streetAddress && site.city && site.stateCode && site.zipcode) {
    return `${site.streetAddress}, ${site.city}, ${site.stateCode} ${site.zipcode}`;
  }
  const lat = site.lat ?? site.latitude;
  const lng = site.lng ?? site.longitude;
  if (lat != null && lng != null) {
    return `${lat},${lng}`;
  }
  // Last resort: whatever address fragments we have (never stringified undefined).
  return [site.streetAddress, site.city, site.stateCode, site.zipcode].filter(Boolean).join(', ');
}

/**
 * The location that list/detail distances and walk times are measured from.
 *
 * When the user has searched for a destination, distances anchor to that searched
 * location so the list reflects what was searched. Otherwise they fall back to the
 * user's current location.
 */
export function getDistanceAnchor(
  selectedSearchLoc: { latitude: number; longitude: number } | null,
  userLocation: { latitude: number; longitude: number } | null,
): { latitude: number; longitude: number } | null {
  if (selectedSearchLoc) {
    return { latitude: selectedSearchLoc.latitude, longitude: selectedSearchLoc.longitude };
  }
  return userLocation;
}

export function buildSitesWithWalk(
  sites: SiteWithHoursAndRate[],
  referenceLocation: { latitude: number; longitude: number } | null,
): SiteWithWalk[] {
  return sites
    .map((site) => {
      if (!referenceLocation)
        return { site, distanceMiles: null, walkTimeMinutes: null, walkDistanceFormatted: null };
      const distanceMiles = getCoordinateDistance(
        [referenceLocation.longitude, referenceLocation.latitude],
        [site.lng, site.lat],
      );
      return {
        site,
        distanceMiles,
        walkTimeMinutes: Math.max(1, Math.round(distanceMiles * 20)),
        walkDistanceFormatted: `${distanceMiles.toFixed(1)}mi`,
      };
    })
    .sort((a, b) => {
      const aDist = a.distanceMiles;
      const bDist = b.distanceMiles;

      if (aDist == null && bDist == null) return 0;
      if (aDist == null) return 1;
      if (bDist == null) return -1;

      return aDist - bDist;
    });
}

export function sortSitesByPrice(sitesWithWalk: SiteWithWalk[]): SiteWithWalk[] {
  return [...sitesWithWalk].sort((a, b) => getSiteRateValue(a.site) - getSiteRateValue(b.site));
}

/**
 * Collapsed label for a selected reservation window, e.g. "Aug 10, 12:00 PM – 2:00 PM"
 * or "Aug 10, 12:00 PM – Aug 11, 2:00 PM" when the range spans multiple days.
 * Returns null until both endpoints are set so callers can fall back (e.g. "Any date & time").
 */
export function formatReservationWindowLabel(start?: Date, end?: Date): string | null {
  if (!start || !end) return null;

  const startDay = dayjs(start);
  const endDay = dayjs(end);

  if (startDay.isSame(endDay, 'day')) {
    return `${startDay.format('MMM D, h:mm A')} – ${endDay.format('h:mm A')}`;
  }

  return `${startDay.format('MMM D, h:mm A')} – ${endDay.format('MMM D, h:mm A')}`;
}

/**
 * Event-rate parking window from availability `zoneDetails.bufferStart` /
 * `bufferEnd`. Prefer this over suggestion event times for the Event rate tag
 * and checkout reservation window. In-progress buffers clamp start forward so
 * locked checkout cannot land on a past start.
 */
export function resolveSiteBufferWindow(
  site: Pick<SiteWithHoursAndRate, 'bufferEnd' | 'bufferStart'>,
  now: Date = new Date(),
): { end: Date; start: Date } | undefined {
  if (!site.bufferStart || !site.bufferEnd) return undefined;
  const start = parseDeepLinkDateTime(site.bufferStart);
  const end = parseDeepLinkDateTime(site.bufferEnd);
  if (!start || !end) return undefined;
  return clampReservationWindowToFuture(start, end, now);
}

/**
 * Lot buffer for Book now / Details only when it already matches the committed
 * discovery window (unanimous consensus). Mixed per-lot buffers keep the quoted
 * start/end so card Totals match checkout.
 */
export function resolveLockedBookWindow(
  site: Pick<SiteWithHoursAndRate, 'bufferEnd' | 'bufferStart'>,
  committed: { end?: Date; start?: Date },
  options?: { locked?: boolean; now?: Date },
): { end: Date; start: Date } | undefined {
  if (!options?.locked || !committed.start || !committed.end) return undefined;
  const buffer = resolveSiteBufferWindow(site, options.now);
  if (!buffer) return undefined;
  if (
    buffer.start.getTime() !== committed.start.getTime() ||
    buffer.end.getTime() !== committed.end.getTime()
  ) {
    return undefined;
  }
  return buffer;
}

/**
 * When every lot with a buffer shares the same clamped window, return it so
 * locked event search can re-quote availability for that parking window.
 * Mixed per-lot buffers stay on the suggestion event times.
 */
export function resolveConsensusSiteBufferWindow(
  sites: readonly Pick<SiteWithHoursAndRate, 'bufferEnd' | 'bufferStart'>[],
  now: Date = new Date(),
): { end: Date; start: Date } | undefined {
  let consensus: { end: Date; start: Date } | undefined;
  for (const site of sites) {
    const buffer = resolveSiteBufferWindow(site, now);
    if (!buffer) continue;
    if (!consensus) {
      consensus = buffer;
      continue;
    }
    if (
      consensus.start.getTime() !== buffer.start.getTime() ||
      consensus.end.getTime() !== buffer.end.getTime()
    ) {
      return undefined;
    }
  }
  return consensus;
}

/**
 * Canonical filter values used by {@link ReservationFiltersSheet}
 * (`self-park`, `valet`, `ev-charger`, `in-and-out-access`, `covered`, `uncovered`).
 *
 * Maps both quote `features` (kebab-case) and zone `amenities` (`AMENITY_*`) onto
 * those values so the filter sheet and amenity chips share one vocabulary.
 */
const FILTER_TAG_ALIASES: Record<string, string> = {
  'self-park': 'self-park',
  selfpark: 'self-park',
  valet: 'valet',
  'ev-charger': 'ev-charger',
  'ev-charging': 'ev-charger',
  'ev-charge': 'ev-charger',
  'in-and-out-access': 'in-and-out-access',
  'in-out-access': 'in-and-out-access',
  covered: 'covered',
  uncovered: 'uncovered',
  accessible: 'accessible',
  'wheelchair-accessible': 'accessible',
};

const DISCOVER_AMENITY_FILTER_TAGS = new Set([
  'ev-charger',
  'wheelchair-accessible',
  'in-out-access',
]);

/** Normalize a raw feature/amenity string to a canonical filter tag, or null if unknown. */
export function canonicalizeFilterTag(raw: string): string | null {
  const normalized = raw.trim().toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
  const isAmenity = normalized.startsWith('amenity-');
  const key = normalized.replace(/^amenity-/, '');
  if (isAmenity && !DISCOVER_AMENITY_FILTER_TAGS.has(key)) return null;
  return FILTER_TAG_ALIASES[key] ?? null;
}

/**
 * Union of filterable tags for a site from quote `features` and zone `amenities`.
 */
export function getSiteFilterTags(site: SiteWithHoursAndRate): Set<string> {
  const tags = new Set<string>();
  for (const raw of [...(site.features ?? []), ...(site.amenities ?? [])]) {
    const canonical = canonicalizeFilterTag(raw);
    if (canonical) tags.add(canonical);
  }
  return tags;
}

function siteHasParkingType(tags: Set<string>, parkingType: string): boolean {
  if (parkingType === 'covered') return tags.has('covered');
  if (parkingType === 'uncovered') return tags.has('uncovered');
  return tags.has(parkingType);
}

/**
 * Client-side discovery filter predicate.
 *
 * - Lot facilities: AND — site must have every selected facility
 *   (from quote `features` and/or zone `amenities`, normalized).
 * - Parking types: OR — site must match at least one selected type.
 * - Sites with no classifiable tags are excluded when any filter is active
 *   (filters must actually reduce results, not no-op via pass-through).
 */
export function siteMatchesFilters(
  site: SiteWithHoursAndRate,
  filters: ReservationFiltersValue,
): boolean {
  const { lotFacilities, parkingTypes } = filters;
  if (lotFacilities.length === 0 && parkingTypes.length === 0) return true;

  const tags = getSiteFilterTags(site);
  if (tags.size === 0) return false;

  const matchesFacilities = lotFacilities.every((value) => tags.has(value));
  if (!matchesFacilities) return false;

  if (parkingTypes.length === 0) return true;
  return parkingTypes.some((value) => siteHasParkingType(tags, value));
}

export interface ReservationWindowValidation {
  /** Which Ends field should show an error border. */
  endInvalid: boolean;
  /** Human-readable message, or null when the window is valid / unset. */
  message: string | null;
  /** Which Starts field should show an error border. */
  startInvalid: boolean;
}

/**
 * Validate an optional reservation window for discovery Find parking flows.
 * Both unset is allowed (browse without a window). Any partial or past window
 * returns copy suitable for inline alert UI.
 */
export function getReservationWindowValidation(
  start?: Date,
  end?: Date,
  now: Date = new Date(),
): ReservationWindowValidation {
  if (!start && !end) {
    return { message: null, startInvalid: false, endInvalid: false };
  }
  if (!start && end) {
    return { message: 'Select a start time', startInvalid: true, endInvalid: false };
  }
  if (start && !end) {
    return { message: 'Select an end time', startInvalid: false, endInvalid: true };
  }
  if (!start || !end) {
    return { message: null, startInvalid: false, endInvalid: false };
  }
  if (start.getTime() <= now.getTime()) {
    return {
      message: 'Start time must be in the future',
      startInvalid: true,
      endInvalid: false,
    };
  }
  if (end.getTime() <= start.getTime()) {
    return {
      message: 'End time must be after the start time',
      startInvalid: false,
      endInvalid: true,
    };
  }
  return { message: null, startInvalid: false, endInvalid: false };
}
